import {AsyncStatusesEnum} from '../../../../libs/shared/helpers/src/types/asyncStatuses';
import {atIndex} from '../../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {CancelToken} from '../../../helpers/asyncCancelHelpers';
import {ActionWithPayload, batch} from '../../../helpers/reduxHelpers';
import {ApiEmlMessageDataGroup, DataGroupModel} from '../../../models/api/dataGroupModel';
import {buildEmlMessageUid, MessageModel} from '../../../models/api/messageModel';
import {fetchApiAsync, postApiAsync} from '../../../services/http/http';
import {findPanelSequence} from '../../../services/panels';
import {buildPanelCancelToken} from '../../../services/panelsCancellers';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../services/tracking';
import {ThunkAsyncAction} from '../../appThunk';
import {getCompany} from '../../data/company/companySelectors';
import {updateDataAction} from '../../data/dataGroupsActions';
import {PanelActionPayload, setPanelAction} from '../panelsActions';
import {makeGetAppPanelIdFromPanel} from '../panelsSelectors';
import {EmlPanelId, makeEmlPanelId, NewPanelId, PanelTypesEnum} from '../panelStateBase';
import {makeGetEmlPanel} from './emlPanelSelectors';
import {EmlSource, EmlSourceTypesEnum} from './emlPanelState';

/*
 * Action type enum.
 */

export enum EmlPanelActionsEnum {
  FETCH_EML_COMPLETED = '@@panels/eml/FETCH_EML_COMPLETED',
  FETCH_EML_FAILED = '@@panels/eml/FETCH_EML_FAILED',
}

/*
 * Action types.
 */

type EmlPanelActionPayload = PanelActionPayload<EmlPanelId>;

interface FetchEmlCompletedActionPayload extends EmlPanelActionPayload {
  messageUid: string;
}

interface FetchEmlCompletedAction extends ActionWithPayload<FetchEmlCompletedActionPayload> {
  type: EmlPanelActionsEnum.FETCH_EML_COMPLETED;
}

interface FetchEmlFailedAction extends ActionWithPayload<EmlPanelActionPayload> {
  type: EmlPanelActionsEnum.FETCH_EML_FAILED;
}

/*
 * Action creators.
 */

export function popoutEmlAsyncAction(panelId: NewPanelId, emlSource: EmlSource): ThunkAsyncAction {
  return async (dispatch, getState) => {
    trackInterfaceEvent(InterfaceEventsEnum.OPEN_EML_FILE);

    const appPanelId = makeGetAppPanelIdFromPanel()(getState(), {panelId});
    const emlPanelId = makeEmlPanelId();
    const [cancelTokenId, cancelToken] = buildPanelCancelToken(emlPanelId);

    const panelState = {
      id: emlPanelId,
      sequence: findPanelSequence(),
      popupAppPanelId: appPanelId,
      shouldForcePopout: true,
      source: emlSource,
      cancelTokenId,
    };

    if (emlSource.type === EmlSourceTypesEnum.ATTACHMENT) {
      const messageUid = buildEmlMessageUid(emlSource.attachmentUid);
      const message = getState().data.messages[messageUid];
      if (message) {
        dispatch(
          setPanelAction({
            ...panelState,
            status: AsyncStatusesEnum.LOADED,
            messageUid,
            type: PanelTypesEnum.EML,
          }),
        );
        return;
      }
    }

    batch(() => {
      dispatch(
        setPanelAction({
          ...panelState,
          status: AsyncStatusesEnum.LOADING,
          messageUid: undefined,
          type: PanelTypesEnum.EML,
        }),
      );
      dispatch(fetchEmlAsyncAction(emlPanelId, emlSource, cancelToken));
    });
  };
}

export function retryEmlAsyncAction(panelId: EmlPanelId): ThunkAsyncAction {
  return (dispatch, getState) => {
    const panel = makeGetEmlPanel()(getState(), {panelId});
    const {source, cancelTokenId} = panel;
    const [newCancelTokenId, cancelToken] = buildPanelCancelToken(panelId, cancelTokenId);

    return batch(() => {
      dispatch(
        setPanelAction({
          ...panel,
          status: AsyncStatusesEnum.LOADING,
          messageUid: undefined,
          cancelTokenId: newCancelTokenId,
        }),
      );
      return dispatch(fetchEmlAsyncAction(panelId, source, cancelToken));
    });
  };
}

function fetchEmlAsyncAction(
  panelId: EmlPanelId,
  emlSource: EmlSource,
  cancelToken: CancelToken,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const company = getCompany(getState());

    try {
      const apiPromise =
        emlSource.type === EmlSourceTypesEnum.FILE
          ? postApiAsync(ApiEmlMessageDataGroup, `${company.url}/eml`, emlSource.emlFileContents, {
              headers: {'content-type': 'message/rfc822'},
              cancelToken,
            })
          : fetchApiAsync(
              ApiEmlMessageDataGroup,
              `${company.url}/attachments/${emlSource.attachmentUid}?action=preview-eml`,
              {cancelToken},
            );

      const fetchedDataGroup = await apiPromise;
      const message = atIndex(fetchedDataGroup.messages || [], 0);
      if (!message) {
        throw new Error('A message could not be found.');
      }

      const {updatedDataGroup, messageUid} = maybeBuildPatchedDataGroup(fetchedDataGroup, message, emlSource);

      batch(() => {
        dispatch(updateDataAction(updatedDataGroup));
        dispatch({
          type: EmlPanelActionsEnum.FETCH_EML_COMPLETED,
          payload: {
            panelId,
            messageUid,
          },
        });
      });
    } catch (error) {
      dispatch({
        type: EmlPanelActionsEnum.FETCH_EML_FAILED,
        payload: {panelId},
      });
    }
  };
}

function maybeBuildPatchedDataGroup(
  fetchedDataGroup: DataGroupModel,
  message: MessageModel,
  emlSource: EmlSource,
) {
  if (emlSource.type === EmlSourceTypesEnum.ATTACHMENT) {
    const messageUid = buildEmlMessageUid(emlSource.attachmentUid);
    const updatedDataGroup = {
      ...fetchedDataGroup,
      messages: [{...message, uid: messageUid}],
    };
    return {updatedDataGroup, messageUid};
  }

  return {updatedDataGroup: fetchedDataGroup, messageUid: message.uid};
}

/*
 * Combined action type.
 */

export type EmlPanelAction = FetchEmlCompletedAction | FetchEmlFailedAction;
